import { useEffect, useState } from 'react';

import { mutate } from 'swr';

import { Button, ModalContent, Timer } from 'components';
import { swrKeys } from 'helpers';
import {
  useAcceptConsignment,
  useAuth,
  useConsignmentsDetails,
  useSuperExpressAudio,
} from 'hooks';
import { consignmentService } from 'services';
import { Consignment, ConsignmentDetails } from 'types';

import { ProductEntry, RefusalReasonSelect } from './components';

interface Props {
  handleClose: () => void;
  consignments: Consignment[];
  setSelectedConsignment: (consignment: Consignment | null) => void;
}

export function SuperExpressModal({
  handleClose,
  consignments,
  setSelectedConsignment,
}: Props) {
  const { selectedStore } = useAuth();
  const store = selectedStore?.name ?? '';

  const { acceptConsignment } = useAcceptConsignment();
  const { stopAudio } = useSuperExpressAudio();

  const { consignmentCode, customerName, endTimer } = consignments[0] || {};

  const [consignmentDetails, setConsignmentDetails] =
    useState<ConsignmentDetails | null>(null);
  const [showRefusalReason, setShowRefusalReason] = useState(false);

  const { data: detailsData } = useConsignmentsDetails(consignmentCode);

  useEffect(() => {
    if (detailsData) {
      setConsignmentDetails(detailsData);
    }
  }, [detailsData]);

  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, [stopAudio]);

  function handleAccept() {
    acceptConsignment({
      consignment: consignments[0],
      setSelectedConsignment,
    });

    handleClose();
  }

  async function handleRefuse(reason?: string) {
    if (!reason) return;

    await consignmentService.putRefuse({
      consignmentCode,
      store,
      reason,
      description: 'Pedido super expresso recusado',
      selectedProducts:
        consignmentDetails?.entries.map(entry => entry.product.legacySKU) || [],
    });

    mutate(swrKeys.consignments({ key: store }));

    handleClose();
  }

  function renderContent() {
    if (showRefusalReason) {
      return (
        <RefusalReasonSelect
          onBack={() => setShowRefusalReason(false)}
          onRefuse={handleRefuse}
        />
      );
    }

    return (
      <>
        <div className="pb-3">
          <p className="text-center text-font10 font-bold text-neutral-500">
            Itens do pedido
          </p>
          {consignmentDetails?.entries?.map((entry, index) => (
            <ProductEntry key={index} entry={entry} />
          ))}
        </div>
        <div className="py-2">
          <p className="text-center text-font14 text-neutral-500">
            Você deverá{' '}
            <span className="font-bold">despachar em até 1 hora</span> desde o
            recebimento (já em andamento).
          </p>
        </div>
        <div className="flex gap-4 py-6">
          <Button
            full
            variant="secondary"
            type="button"
            onClick={() => setShowRefusalReason(true)}
          >
            Recusar
          </Button>
          <Button full type="button" onClick={handleAccept}>
            Aceitar
          </Button>
        </div>
      </>
    );
  }

  return (
    <ModalContent handleClose={handleClose} padding="0" closable={false}>
      <div
        className="flex w-[443px] flex-col overflow-hidden rounded-[5px]
          bg-neutral-100"
      >
        <div className="bg-danger-500 px-6 py-2">
          <p className="text-font16 font-bold text-neutral-100">
            Pedido Super Expresso
          </p>
          <p className="text-font12 text-neutral-100">
            Pedido {consignmentCode} - {customerName}
          </p>
        </div>

        <div className="px-6">
          <div className="flex flex-col items-center py-6">
            <p className="text-font12 font-bold text-neutral-500">
              Tempo para aceitar:
            </p>
            <Timer className="text-danger-500" endTimer={endTimer} />
          </div>

          {renderContent()}
        </div>
      </div>
    </ModalContent>
  );
}
